/**
 * Order SAP Sync Tab
 *
 * Displays SAP sync job history for an order.
 * Searches by the order's numeric Strapi ID and SAP sales order number (if present).
 * Visible only to super admins.
 * Self-contained — manages its own data fetch lifecycle.
 */

import { useState, useEffect } from 'react'
import { useTranslation } from '@spcc/core'
import { Loader2, RefreshCw } from '@spcc/uikit'
import { cn } from '@spcc/uikit'
import type { Order } from '../../domain/types'
import type { SapSyncJob, SapOperation } from '@spcc/domains/sap-sync'
import { sapSyncApi, SyncJobsTable, SyncJobDetailDrawer } from '@spcc/domains/sap-sync'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ORDER_SAP_OPERATIONS: SapOperation[] = [
  'create_sales_order',
  'weight_update_first',
  'weight_update_second',
]

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function buildEntityIds(order: Order): string[] {
  const ids: string[] = []
  if (order.numericId != null) {
    ids.push(String(order.numericId))
  }
  if (order.sapSalesOrderNumber) {
    ids.push(order.sapSalesOrderNumber)
  }
  return ids
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface OrderSapSyncTabProps {
  order: Order
}

export function OrderSapSyncTab({ order }: OrderSapSyncTabProps) {
  const { t } = useTranslation()

  const [jobs, setJobs] = useState<SapSyncJob[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedJob, setSelectedJob] = useState<SapSyncJob | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)

  const entityIds = buildEntityIds(order)

  const fetchJobs = async () => {
    if (entityIds.length === 0) return
    setLoading(true)
    try {
      const result = await sapSyncApi.getSyncJobs({
        entityIds,
        sapOperations: ORDER_SAP_OPERATIONS,
        sortField: 'createdAt',
        sortOrder: 'desc',
      })
      setJobs(result.data)
    } catch {
      setJobs([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchJobs()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.id])

  const handleRowClick = (job: SapSyncJob) => {
    setSelectedJob(job)
    setDetailOpen(true)
  }

  if (entityIds.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <RefreshCw className="w-10 h-10 text-muted-foreground/30 mb-3" />
        <p className="text-sm font-medium text-muted-foreground">
          {t('orders.sapSyncNoJobs')}
        </p>
        <p className="text-xs text-muted-foreground/70 mt-1">
          {t('orders.sapSyncNoJobsDescription')}
        </p>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  if (jobs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <RefreshCw className="w-10 h-10 text-muted-foreground/30 mb-3" />
        <p className="text-sm font-medium text-muted-foreground">
          {t('orders.sapSyncNoJobs')}
        </p>
        <p className="text-xs text-muted-foreground/70 mt-1">
          {t('orders.sapSyncNoJobsDescription')}
        </p>
        <button
          onClick={fetchJobs}
          className={cn(
            'mt-4 inline-flex items-center gap-2 text-xs text-primary hover:underline',
          )}
        >
          <RefreshCw className="w-3.5 h-3.5" />
          {t('sapSync.refresh')}
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          {jobs.length} {t('sapSync.records')}
        </p>
        <button
          onClick={fetchJobs}
          disabled={loading}
          className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
        >
          <RefreshCw className={cn('w-3.5 h-3.5', loading && 'animate-spin')} />
          {t('sapSync.refresh')}
        </button>
      </div>

      <SyncJobsTable
        jobs={jobs}
        selectedRowId={selectedJob != null ? String(selectedJob.id) : null}
        onRowClick={handleRowClick}
      />

      <SyncJobDetailDrawer
        job={selectedJob}
        open={detailOpen}
        onOpenChange={setDetailOpen}
      />
    </div>
  )
}
